import React, { Ref, useCallback, useEffect, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { useActionFolders } from "Hooks/useActionFolders";
import { useTranslation } from "react-i18next";
import { FiActivity } from "react-icons/fi";

import SideViewComponent from "../../components/Generic/ViewJobComponent";
import { useAppState } from "../../context/StateContext";
import { useActionsJob } from "../../hooks/useActionJob";
import { Order } from "../../models/order";
import { User } from "../../models/user";
import Details from "../../pages/private/Moving/Operations/Maintenance/Details/ViewMode";
import OrderInventory from "../../pages/private/Moving/Operations/Maintenance/Inventory";
import TasksTab from "../../pages/private/Moving/Operations/Maintenance/Tasks";
import WaybillTab from "../../pages/private/Moving/Operations/Maintenance/Waybill";
import OrderService from "../../services/order.service";
import { ItemJobSubStatus, ResourceName } from "../../util/enums";
import { getOrderFolder } from "../../util/getFolders";
import getItemsToHistory from "../../util/getHistory";
import { getOrderCustomerDisplayName } from "../../util/names";
import { Color } from "../Form/Button";
import AccessViewMode from "../Generic/Access/ViewMode";
import Files from "../Generic/Files";
import History from "../Generic/History";
import Instructions from "../Generic/Instructions";
import PackingMaterials from "../Generic/PackingMaterials";
import { Navigation, PropHandles } from "../Generic/ViewJobComponent";
import SkeletonDetails from "./SkeletonDetails";

interface Props {
  jobId?: number;
  sideViewRef: Ref<PropHandles>;
  onCloseModal: () => void;
}

export const SidebarViewOrder = ({ jobId, sideViewRef, onCloseModal }: Props) => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const [title, setTitle] = useState<string>("Order");
  const [order, setOrder] = useState<Order | undefined>();

  const { getInventoryList, getJobHistories } = useActionsJob({
    jobId,
  });

  const { data: historyData } = getJobHistories();

  const { getOrderFolders } = useActionFolders({ jobId });

  const { data: foldersData } = getOrderFolders();

  const { data: inventoryData, isLoading: isLoadingInventory } = getInventoryList([
    ItemJobSubStatus.QUOTED,
  ]);

  const { data: actualInventoryData, isLoading: isLoadingActualInventory } = getInventoryList([
    ItemJobSubStatus.ACTUAL,
  ]);

  const { refetch: getOrder, isFetching: loadingOrder } = useQuery(
    ["orders.get", jobId],
    () =>
      OrderService.getById(Number(jobId), {
        isSalesApp: true,
        isNewVersion: true,
      }),
    {
      enabled: !!jobId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          setOrder((currentOrder) =>
            getOrderFolder(translate, { ...currentOrder, ...response.data })
          );
        }
      },
    }
  );

  useEffect(() => {
    if (inventoryData || actualInventoryData) {
      setOrder((currentOrder) =>
        getOrderFolder(translate, {
          ...currentOrder,
          inventory: inventoryData?.quotedInventory || [],
          actualInventory: actualInventoryData?.actualInventory || [],
        })
      );
    }
  }, [inventoryData, actualInventoryData]);

  useEffect(() => {
    if (historyData && historyData?.length) {
      setOrder((currentOrder) => {
        return { ...currentOrder, histories: historyData };
      });
    }
  }, [historyData]);

  useEffect(() => {
    if (order) {
      setTitle(getSidebarTitle());
    }
  }, [order]);

  useEffect(() => {
    resetStates();
  }, [jobId]);

  const resetStates = () => {
    setOrder(undefined);
    setTitle("Order");
  };

  const countMaterial = (): number => {
    let materials = 0;

    order?.inventory?.forEach((inventory) => {
      if (inventory.materials.length > 0) materials += 1;
    });

    return materials;
  };

  const getSidebarTitle = (): string => {
    let title = "Order";

    const reference = order?.reference ?? order?.reference;
    const fullName = getOrderCustomerDisplayName(order);

    if (reference) title = `${title} #${reference}`;
    if (fullName) {
      title = `${title} - ${fullName}`;
    }

    return title;
  };

  const handleSetNavigation = useCallback(() => {
    const navList = [
      { key: Navigation.DETAILS },
      { key: Navigation.INVENTORY },
      { key: Navigation.MATERIAL },
      { key: Navigation.TASKS },
      { key: Navigation.ACCESS },
      { key: Navigation.INSTRUCTIONS },
      { key: Navigation.SHIPMENT },
      { key: Navigation.FOLDERS },
    ];

    return navList;
  }, [order]);

  const handleSetTabs = useCallback(() => {
    const tabs = [
      {
        key: Navigation.DETAILS,
        loading: loadingOrder,
        isEmpty: !order,
        render: () => <Details order={order} />,
        renderLoading: () => <SkeletonDetails />,
      },
      {
        key: Navigation.INVENTORY,
        loading: loadingOrder || isLoadingInventory || isLoadingActualInventory || (!!order?.id && order?.actualInventory === undefined),
        isEmpty:
          (!order?.inventory && !order?.actualInventory) ||
          (order?.inventory?.length === 0 && order?.actualInventory?.length === 0),
        render: () => (
          <OrderInventory
            order={order}
            inventory={order?.inventory || []}
            quoteJobId={order?.jobId || 0}
            defaultMode={order?.mode || ""}
            destinations={order?.destination?.locations || []}
            actualInventory={order?.actualInventory || []}
            callbackFunction={getOrder}
            storageBoxes={order?.storageBoxes}
            quotedViewOnly
            isOnViewMode
          />
        ),
      },
      {
        key: Navigation.MATERIAL,
        loading: loadingOrder || isLoadingInventory || isLoadingActualInventory,
        isEmpty: !order?.inventory || countMaterial() === 0,
        renderLoading: () => <SkeletonDetails />,
        render: () => <PackingMaterials inventory={order?.inventory} isOnViewMode />,
      },
      {
        key: Navigation.TASKS,
        loading: loadingOrder,
        isEmpty:
          (!order?.tasks && !order?.taskDates) ||
          (order?.tasks?.length === 0 && order?.taskDates?.length === 0),
        render: () => <TasksTab tasks={order?.tasks} taskDates={order?.taskDates} isOnViewMode />,
      },
      {
        key: Navigation.ACCESS,
        loading: loadingOrder,
        isEmpty: !order?.pickupAccess && !order?.deliveryAccess,
        renderLoading: () => <SkeletonDetails />,
        render: () => (
          <AccessViewMode
            pickupAccess={order?.pickupAccess}
            deliveryAccess={order?.deliveryAccess}
            pickupAddress={order?.origin?.locations?.[0]}
            deliveryAddress={order?.destination?.locations?.[0]}
          />
        ),
      },
      {
        key: Navigation.INSTRUCTIONS,
        loading: loadingOrder,
        isEmpty: !order?.instructions || order?.instructions?.length === 0,
        renderLoading: () => <SkeletonDetails />,
        render: () => <Instructions isOnViewMode instructions={order?.instructions || []} />,
      },
      {
        key: Navigation.SHIPMENT,
        loading: loadingOrder,
        isEmpty: order?.shipments?.length === 0,
        render: () => <WaybillTab waybills={order?.shipments ?? []} isOnViewMode />,
      },
      {
        key: Navigation.FOLDERS,
        render: () => (
          <Files
            isOnViewMode
            folders={foldersData}
            foldersDeleted={order?.foldersDeleted}
            filesDeleted={order?.filesDeleted}
            typeFolder={ResourceName.ORDERS}
          />
        ),
      },
    ];

    return tabs;
  }, [order, loadingOrder, isLoadingInventory, isLoadingActualInventory, foldersData]);

  const handleSetButtonsComponent = useCallback(() => {
    return [
      {
        buttonLabel: translate("seeActivity"),
        titlePage: translate("activity"),
        color: Color.SECONDARY,
        icon: <FiActivity size={16} />,
        renderLoading: () => <SkeletonDetails />,
        renderComponent: () => (
          <History
            items={getItemsToHistory(currentUser ?? ({} as User), order?.id, order?.histories)}
          />
        ),
      },
    ];
  }, [order]);

  return (
    <SideViewComponent
      ref={sideViewRef}
      onClose={() => {
        onCloseModal();
        resetStates();
      }}
      job={order}
      isLoading={loadingOrder}
      title={title}
      navigation={handleSetNavigation()}
      tabs={handleSetTabs()}
      buttonsComponent={handleSetButtonsComponent()}
    />
  );
};
